import Dexie from 'dexie'
import {
  User,
  Order,
  OrderDetails,
  MenuItem,
  Inventory,
  ComplaintFeedback,
  Admin,
  Delivery,
  Payment,
  PromoIncentive,
  Menu,
} from '../types/database'
class FoodAIDatabase extends Dexie {
  users: Dexie.Table<User, number>
  orders: Dexie.Table<Order, string>
  orderDetails: Dexie.Table<OrderDetails, number>
  menuItems: Dexie.Table<MenuItem, number>
  inventory: Dexie.Table<Inventory, number>
  complaints: Dexie.Table<ComplaintFeedback, number>
  admins: Dexie.Table<Admin, number>
  deliveries: Dexie.Table<Delivery, number>
  payments: Dexie.Table<Payment, number>
  promos: Dexie.Table<PromoIncentive, number>
  menus: Dexie.Table<Menu, number>
  constructor() {
    super('FoodAIDatabase')
    this.version(1).stores({
      users:
        '++User_ID, Name, Email, Password, Contact, Address, User_type, Date_Registered',
      orders: 'OrderID, User_ID, Order_Date, Total_Amount, Order_Status',
      orderDetails: '++OrderDetail_ID, Order_ID, Item_ID, Quantity, Subtotal',
      menuItems:
        '++ItemID, Item_Name, Description, Availability, Category, Price',
      inventory: '++Inventory_ID, Item_Name, Quantity, Unit, LastUpdate',
      complaints:
        '++TicketNum, User_ID, OrderID, Description, Status, Date_Submitted',
      admins: '++OrderDetail_ID, Name, Email, Password, Role',
      deliveries:
        '++DeliveryID, Order_ID, DeliveryStaffID, DeliveryAddress, Delivery_Status, EstimatedTime',
      payments:
        '++PaymentID, Order_ID, Payment_Method, Transaction_Date, Payment_Status',
      promos:
        '++PromoID, Promo_Type, Description, Start_Date, End_Date, Eligibility',
      menus: 'Item_Name, Description, Price, Category, Availability',
    })
  }
  // Helper methods for initializing with sample data
  async initializeDatabase() {
    // Check if database is already initialized
    const userCount = await this.users.count()
    if (userCount > 0) {
      console.log('Database already initialized')
      return
    }
    console.log('Initializing database with sample data...')
    try {
      // Sample data initialization
      await this.populateSampleData()
      console.log('Database initialized successfully')
    } catch (error) {
      console.error('Error initializing database:', error)
    }
  }
  async populateSampleData() {
    // HARDCODED ADMIN ACCOUNT - DO NOT MODIFY
    const ADMIN_EMAIL = 'admin@account.com'
    const ADMIN_PASSWORD = 'admin1234'
    // Sample users
    await this.users.bulkAdd([
      {
        Name: 'System Administrator',
        Email: ADMIN_EMAIL,
        Password: ADMIN_PASSWORD,
        Contact: '09999999999',
        Address: 'Admin Office',
        User_type: 'admin',
        Date_Registered: new Date().toISOString(),
      },
      {
        Name: 'John Doe',
        Email: 'john@example.com',
        Password: 'password123',
        Contact: '09123456789',
        Address: '123 Main St, Manila',
        User_type: 'customer',
        Date_Registered: new Date().toISOString(),
      },
      {
        Name: 'Delivery Staff',
        Email: 'delivery@example.com',
        Password: 'delivery123',
        Contact: '09555123456',
        Address: '789 Delivery St, Manila',
        User_type: 'delivery',
        Date_Registered: new Date().toISOString(),
      },
    ])
    // Sample menu items
    await this.menuItems.bulkAdd([
      {
        ItemID: 1,
        Item_Name: 'Kare-Kare',
        Description:
          'A Filipino stew with a thick peanut sauce, oxtail, and vegetables.',
        Availability: 'Available',
        Category: 'Main Dishes',
        Price: 299.0,
      },
      {
        ItemID: 2,
        Item_Name: 'Bulalo',
        Description:
          'A light-colored soup made by cooking beef shanks and bone marrow with corn, cabbage, green beans and potatoes.',
        Availability: 'Available',
        Category: 'Main Dishes',
        Price: 320.0,
      },
      {
        ItemID: 3,
        Item_Name: 'Sinigang',
        Description: 'A sour soup with pork, vegetables, and tamarind.',
        Availability: 'Available',
        Category: 'Main Dishes',
        Price: 280.0,
      },
      {
        ItemID: 4,
        Item_Name: 'Lumpiang Shanghai',
        Description:
          'Filipino spring rolls filled with ground pork and vegetables.',
        Availability: 'Low Stock',
        Category: 'Side Dishes',
        Price: 150.0,
      },
      {
        ItemID: 5,
        Item_Name: 'Halo-Halo',
        Description:
          'A popular Filipino dessert with mixed sweets, shaved ice, and ice cream.',
        Availability: 'Available',
        Category: 'Desserts',
        Price: 130.0,
      },
    ])
    // Sample inventory
    await this.inventory.bulkAdd([
      {
        Item_Name: 'Kare-Kare',
        Quantity: 50,
        Unit: 'serving',
        LastUpdate: new Date().toISOString(),
      },
      {
        Item_Name: 'Bulalo',
        Quantity: 40,
        Unit: 'serving',
        LastUpdate: new Date().toISOString(),
      },
      {
        Item_Name: 'Sinigang',
        Quantity: 45,
        Unit: 'serving',
        LastUpdate: new Date().toISOString(),
      },
      {
        Item_Name: 'Lumpiang Shanghai',
        Quantity: 15,
        Unit: 'serving',
        LastUpdate: new Date().toISOString(),
      },
      {
        Item_Name: 'Halo-Halo',
        Quantity: 30,
        Unit: 'serving',
        LastUpdate: new Date().toISOString(),
      },
    ])
    // Sample orders
    await this.orders.bulkAdd([
      {
        OrderID: 'ORD-12345',
        User_ID: 1,
        Order_Date: '2023-08-15 14:30',
        Total_Amount: 619.0,
        Order_Status: 'Delivered',
      },
      {
        OrderID: 'ORD-12346',
        User_ID: 1,
        Order_Date: '2023-08-15 15:45',
        Total_Amount: 450.0,
        Order_Status: 'Preparing',
      },
      {
        OrderID: 'ORD-12347',
        User_ID: 1,
        Order_Date: '2023-08-15 16:20',
        Total_Amount: 300.0,
        Order_Status: 'Pending',
      },
    ])
    // Sample order details
    await this.orderDetails.bulkAdd([
      {
        Order_ID: 'ORD-12345',
        Item_ID: 1,
        Quantity: 1,
        Subtotal: 299.0,
      },
      {
        Order_ID: 'ORD-12345',
        Item_ID: 4,
        Quantity: 2,
        Subtotal: 300.0,
      },
      {
        Order_ID: 'ORD-12346',
        Item_ID: 2,
        Quantity: 1,
        Subtotal: 320.0,
      },
      {
        Order_ID: 'ORD-12346',
        Item_ID: 5,
        Quantity: 1,
        Subtotal: 130.0,
      },
      {
        Order_ID: 'ORD-12347',
        Item_ID: 3,
        Quantity: 1,
        Subtotal: 280.0,
      },
    ])
    // Sample complaints
    await this.complaints.bulkAdd([
      {
        User_ID: 1,
        OrderID: 'ORD-12340',
        Description: 'I ordered Kare-Kare but received Caldereta instead.',
        Status: 'Open',
        Date_Submitted: '2023-08-14',
      },
      {
        User_ID: 1,
        OrderID: 'ORD-12335',
        Description:
          'My order was delivered 45 minutes later than the estimated time.',
        Status: 'In Progress',
        Date_Submitted: '2023-08-13',
      },
      {
        User_ID: 1,
        OrderID: 'ORD-12330',
        Description: 'The Bulalo soup was cold upon arrival.',
        Status: 'Resolved',
        Date_Submitted: '2023-08-12',
      },
    ])
    // Sample promos
    await this.promos.bulkAdd([
      {
        PromoID: 1,
        Promo_Type: 'Discount',
        Description: 'First-time registration discount',
        Start_Date: '2023-01-01',
        End_Date: '2023-12-31',
        Eligibility: 'First-time user',
      },
      {
        PromoID: 2,
        Promo_Type: 'Discount',
        Description: 'Summer special promo',
        Start_Date: '2023-03-01',
        End_Date: '2023-05-31',
        Eligibility: 'All users',
      },
      {
        PromoID: 3,
        Promo_Type: 'Free Condiment',
        Description: 'Free delivery for orders above ₱500',
        Start_Date: '2023-01-01',
        End_Date: '2023-12-31',
        Eligibility: 'Minimum order amount ₱500',
      },
    ])
    // Sample deliveries
    await this.deliveries.bulkAdd([
      {
        Order_ID: 'ORD-12345',
        DeliveryStaffID: 3,
        DeliveryAddress: '123 Main St, Manila',
        Delivery_Status: 'Delivered',
        EstimatedTime: '30 minutes',
      },
      {
        Order_ID: 'ORD-12346',
        DeliveryStaffID: 3,
        DeliveryAddress: '123 Main St, Manila',
        Delivery_Status: 'Out for Delivery',
        EstimatedTime: '20 minutes',
      },
    ])
    // Sample payments
    await this.payments.bulkAdd([
      {
        Order_ID: 'ORD-12345',
        Payment_Method: 'Cash',
        Transaction_Date: '2023-08-15 15:00',
        Payment_Status: 'Paid',
      },
      {
        Order_ID: 'ORD-12346',
        Payment_Method: 'Card',
        Transaction_Date: '2023-08-15 16:00',
        Payment_Status: 'Paid',
      },
      {
        Order_ID: 'ORD-12347',
        Payment_Method: 'GCash',
        Transaction_Date: '2023-08-15 16:30',
        Payment_Status: 'Pending',
      },
    ])
  }
}
const db = new FoodAIDatabase()
export default db
